import ImageTokenPricingDetails from '../../../components/ImageTokenPricing';
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Tag, Input, Select, Tooltip } from 'antd';
import {
  mergePricingData,
  formatPrice,
  getPricingTypeName,
} from '../utils/mergePricingData';
import { getCachePriceRows } from '../utils/pricingDisplay';
import '../styles/playground.css';

const { Search } = Input;
const { Option } = Select;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseCreatedAtToDay = (createdAt) => {
  if (!createdAt || createdAt === '0001-01-01T00:00:00Z') {
    return null;
  }
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime();
};

const formatDay = (dayTs) => {
  if (!dayTs) {
    return '-';
  }
  const date = new Date(dayTs);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PricingTablePanel = ({ config, pricingConfig, onBack }) => {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [modelCategoryFilter, setModelCategoryFilter] = useState('all');

  // 合并数据并转换为表格格式
  useEffect(() => {
    if (config && pricingConfig) {
      const merged = mergePricingData(config, pricingConfig);
      const rows = [];

      merged.categories.forEach((category) => {
        category.vendors.forEach((vendor) => {
          vendor.models.forEach((model) => {
            if (model.pricing && model.pricing.enabled) {
              rows.push({
                key: model.id,
                modelId: model.id,
                modelName: model.name,
                vendor: model.vendor || vendor.name,
                category: category.name,
                categoryId: category.id,
                modelCategory: model.category || '',
                pricingType: model.pricing.type,
                enabled: model.pricing.enabled,
                inputPrice: model.pricing.input_price,
                outputPrice: model.pricing.output_price,
                price:
                  model.pricing.price ||
                  model.pricing.base_price ||
                  model.pricing.price_per_char ||
                  model.pricing.price_per_second ||
                  model.pricing.price_per_request ||
                  model.pricing.credit_price,
                description: model.description || model.pricing.description,
                docUrl: model.docUrl || model.pricing?.url,
                fromPlayground: model.fromPlayground,
                createdAt: model.pricing.created_at || '',
                pricing: model.pricing,
              });
            }
          });
        });
      });

      setTableData(rows);
      setFilteredData(rows);
    }
  }, [config, pricingConfig]);

  // 搜索和筛选
  useEffect(() => {
    let filtered = tableData;

    // 用途筛选
    if (modelCategoryFilter !== 'all') {
      filtered = filtered.filter(
        (item) => item.modelCategory === modelCategoryFilter
      );
    }

    // 厂商筛选
    if (vendorFilter !== 'all') {
      filtered = filtered.filter((item) => item.vendor === vendorFilter);
    }

    // 搜索
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.modelId.toLowerCase().includes(searchLower) ||
          item.modelName.toLowerCase().includes(searchLower) ||
          item.vendor.toLowerCase().includes(searchLower)
      );
    }

    setFilteredData(filtered);
  }, [searchText, modelCategoryFilter, vendorFilter, tableData]);

  const newMarkedModelIds = useMemo(() => {
    if (!tableData.length) {
      return new Set();
    }

    const todayStart = new Date();
    const todayStartTs = new Date(
      todayStart.getFullYear(),
      todayStart.getMonth(),
      todayStart.getDate()
    ).getTime();

    const rowsWithDay = tableData
      .map((row) => ({
        modelId: row.modelId,
        dayTs: parseCreatedAtToDay(row.createdAt),
      }))
      .filter((row) => row.dayTs !== null);

    if (!rowsWithDay.length) {
      return new Set();
    }

    const within7Days = rowsWithDay.filter((row) => {
      const dayDiff = Math.floor((todayStartTs - row.dayTs) / MS_PER_DAY);
      return dayDiff >= 0 && dayDiff <= 2;
    });

    if (within7Days.length > 0) {
      return new Set(within7Days.map((row) => row.modelId));
    }

    const latestDayTs = Math.max(...rowsWithDay.map((row) => row.dayTs));
    return new Set(
      rowsWithDay
        .filter((row) => row.dayTs === latestDayTs)
        .map((row) => row.modelId)
    );
  }, [tableData]);

  // 获取所有厂商列表
  const getVendors = () => {
    const vendors = new Set(tableData.map((item) => item.vendor));
    return Array.from(vendors).sort();
  };

  // 获取所有用途列表
  const getModelCategories = () => {
    const categories = new Set(
      tableData.map((item) => item.modelCategory).filter((c) => c)
    );
    return Array.from(categories).sort();
  };

  // 渲染价格单元格
  const renderPrice = (pricing) => {
    if (!pricing) return '-';

    const formatPriceWithUnit = (price, unit) => {
      if (!price) return '-';
      const value = formatPrice(price);
      return value !== 'N/A' ? `${value}${unit}` : '-';
    };

    switch (pricing.type) {
    case 'image_tokens': return <ImageTokenPricingDetails pricing={pricing} />;
      case 'simple':
        return (
          <div className='pricing-cell'>
            <div className='pricing-row'>
              <span className='pricing-label'>输入:</span>
              <span className='pricing-value'>
                {formatPriceWithUnit(pricing.input_price, '/M token')}
              </span>
            </div>
            <div className='pricing-row'>
              <span className='pricing-label'>输出:</span>
              <span className='pricing-value'>
                {formatPriceWithUnit(pricing.output_price, '/M token')}
              </span>
            </div>
            {getCachePriceRows(pricing).map((row) => (
              <div key={row.key} className='pricing-row'>
                <span className='pricing-label'>{row.label}:</span>
                <span className='pricing-value'>
                  {formatPriceWithUnit(row.price, '/M token')}
                </span>
              </div>
            ))}
            {pricing.context_rules && pricing.context_rules.length > 0 && (
              <div className='pricing-conditional-detail'>
                {pricing.context_rules.map((rule, index) => {
                  const tags = [];
                  if (rule.when?.has_audio === true) tags.push('有音频');
                  if (rule.when?.has_audio === false) tags.push('无音频');
                  if (rule.when?.has_reference_video === true)
                    tags.push('有参考视频');
                  if (rule.when?.has_reference_video === false)
                    tags.push('无参考视频');
                  if (rule.when?.resolution)
                    tags.push(`分辨率 ${rule.when.resolution}`);
                  const title =
                    rule.description || tags.join(' / ') || `规则 ${index + 1}`;
                  return (
                    <div key={index} className='pricing-tier'>
                      <span className='pricing-tier-range'>{title}:</span>
                      <span className='pricing-tier-value'>
                        {rule.override?.input_price &&
                          `输入 ${formatPriceWithUnit(
                            rule.override.input_price,
                            '/M token'
                          )}`}
                        {rule.override?.input_price &&
                          rule.override?.output_price &&
                          ' / '}
                        {rule.override?.output_price &&
                          `输出 ${formatPriceWithUnit(
                            rule.override.output_price,
                            '/M token'
                          )}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'conditional':
        return (
          <div className='pricing-cell'>
            {pricing.base_input_price && pricing.base_output_price && (
              <div className='pricing-row'>
                <span className='pricing-label'>基础:</span>
                <span className='pricing-value'>
                  输入{' '}
                  {formatPriceWithUnit(pricing.base_input_price, '/M token')} /
                  输出{' '}
                  {formatPriceWithUnit(pricing.base_output_price, '/M token')}
                </span>
              </div>
            )}
            {pricing.conditional_rules &&
              pricing.conditional_rules.length > 0 && (
                <div className='pricing-conditional-detail'>
                  {pricing.conditional_rules.map((rule, index) => {
                    // 构建范围显示文本
                    let rangeText = '';

                    // 输入范围
                    if (
                      rule.input_min !== undefined &&
                      rule.input_max !== undefined
                    ) {
                      if (rule.input_max === -1) {
                        rangeText = `输入≥${(rule.input_min / 1000).toFixed(
                          0
                        )}K`;
                      } else {
                        rangeText = `输入${(rule.input_min / 1000).toFixed(
                          0
                        )}K-${(rule.input_max / 1000).toFixed(0)}K`;
                      }
                    }

                    // 输出范围
                    if (
                      rule.output_min !== undefined &&
                      rule.output_max !== undefined
                    ) {
                      if (rangeText) rangeText += ', ';
                      if (rule.output_max === -1) {
                        rangeText += `输出≥${rule.output_min}`;
                      } else {
                        rangeText += `输出${rule.output_min}-${rule.output_max}`;
                      }
                    }

                    // 如果有描述，优先使用描述
                    if (rule.description) {
                      rangeText = rule.description;
                    }

                    return (
                      <div key={index} className='pricing-tier'>
                        <span className='pricing-tier-range'>{rangeText}:</span>
                        <span className='pricing-tier-value'>
                          输入{' '}
                          {formatPriceWithUnit(rule.input_price, '/M token')} /
                          输出{' '}
                          {formatPriceWithUnit(rule.output_price, '/M token')}
                          {(() => {
                            const cacheText = getCachePriceRows(rule)
                              .map(
                                (row) =>
                                  `${row.label} ${formatPriceWithUnit(
                                    row.price,
                                    '/M token'
                                  )}`
                              )
                              .join(' / ');
                            return cacheText ? ` / ${cacheText}` : '';
                          })()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>
        );

      case 'matrix':
        const matrix = pricing.price_matrix || {};
        return (
          <div className='pricing-cell'>
            <div className='pricing-matrix-detail'>
              {Object.entries(matrix)
                .slice(0, 6)
                .map(([key, value]) => {
                  // 处理嵌套结构: { "1080p": { "5s": {usd: 1}, "10s": {usd: 1.9} } }
                  const innerEntries = Object.entries(value).slice(0, 3);
                  return (
                    <div key={key} className='pricing-matrix-item'>
                      <span className='pricing-matrix-key'>{key}:</span>
                      <span className='pricing-matrix-value'>
                        {innerEntries.map(([innerKey, innerValue]) => (
                          <span key={innerKey} className='pricing-matrix-inner'>
                            {innerKey}: {formatPrice(innerValue)}
                          </span>
                        ))}
                      </span>
                    </div>
                  );
                })}
              {Object.keys(matrix).length > 6 && (
                <div className='pricing-matrix-more'>
                  ...还有 {Object.keys(matrix).length - 6} 项
                </div>
              )}
            </div>
          </div>
        );

      case 'multiplier':
        const multiplierUnit = pricing.duration_unit ? '/秒' : '/张';
        return (
          <div className='pricing-cell'>
            <div className='pricing-row'>
              <span className='pricing-label'>单次:</span>
              <span className='pricing-value'>
                {formatPriceWithUnit(pricing.base_price, multiplierUnit)}
              </span>
            </div>
            {pricing.resolution_ratios &&
              Object.keys(pricing.resolution_ratios).length > 0 && (
                <div className='pricing-multiplier-detail'>
                  {Object.entries(pricing.resolution_ratios).map(
                    ([key, ratio]) => (
                      <div key={key} className='pricing-multiplier-item'>
                        <span className='pricing-multiplier-key'>{key}:</span>
                        <span className='pricing-multiplier-value'>
                          x{ratio}
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}
            {pricing.duration_unit && (
              <div className='pricing-note'>
                基础时长: {pricing.duration_unit}秒
              </div>
            )}
          </div>
        );

      case 'per_char':
        return (
          <div className='pricing-cell'>
            {formatPriceWithUnit(
              pricing.price_per_char || pricing.price,
              '/1K字符'
            )}
          </div>
        );

      case 'per_second':
        return (
          <div className='pricing-cell'>
            {formatPriceWithUnit(
              pricing.price_per_second || pricing.price,
              '/秒'
            )}
          </div>
        );

      case 'per_request':
        return (
          <div className='pricing-cell'>
            {formatPriceWithUnit(
              pricing.price_per_request || pricing.price,
              '/次'
            )}
          </div>
        );

      case 'credit':
        return (
          <div className='pricing-cell'>
            <div className='pricing-row'>
              <span className='pricing-value'>
                {formatPriceWithUnit(pricing.credit_price, '/credit')}
              </span>
            </div>
            {pricing.credit_estimate?.summary && (
              <div className='pricing-credit-estimate'>
                {pricing.credit_estimate.summary}
              </div>
            )}
          </div>
        );

      default:
        return <div className='pricing-cell'>-</div>;
    }
  };

  const columns = [
    {
      title: '模型名称',
      dataIndex: 'modelId',
      key: 'modelId',
      width: 200,
      fixed: 'left',
      render: (text, record) => (
        <div className='model-name-cell'>
          {text}
          {newMarkedModelIds.has(record.modelId) && (
            <span className='model-new-badge'>NEW</span>
          )}
        </div>
      ),
    },
    {
      title: '厂商',
      dataIndex: 'vendor',
      key: 'vendor',
      width: 120,
    },
    {
      title: '用途',
      dataIndex: 'modelCategory',
      key: 'modelCategory',
      width: 100,
      render: (text) => text || '-',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (text) => (
        <div className='pricing-description-cell'>{text || '-'}</div>
      ),
    },
    {
      title: '文档',
      key: 'docUrl',
      width: 100,
      render: (_, record) =>
        record.docUrl ? (
          <a
            href={record.docUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='pricing-doc-link'
          >
            官方文档
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: '计费类型',
      dataIndex: 'pricingType',
      key: 'pricingType',
      width: 120,
      render: (type) => getPricingTypeName(type),
    },
    {
      title: '价格',
      key: 'price',
      width: 300,
      render: (_, record) => renderPrice(record.pricing),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      onCell: () => ({ className: 'pricing-created-cell' }),
      onHeaderCell: () => ({ className: 'pricing-created-header' }),
      defaultSortOrder: 'descend',
      showSorterTooltip: false,
      sorter: (a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeA - timeB;
      },
      render: (text) => {
        const dayTs = parseCreatedAtToDay(text);
        const dayText = dayTs ? formatDay(dayTs) : '暂无';
        const tipText = dayTs
          ? `模型计费创建日期：${dayText}`
          : '暂无模型计费创建日期';
        return (
          <Tooltip
            title={tipText}
            overlayClassName='pricing-created-tooltip'
            rootClassName='pricing-created-tooltip'
          >
            <span className='pricing-created-tag'>{dayText}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <div>
          {record.enabled ? (
            <Tag color='success'>已启用</Tag>
          ) : (
            <Tag color='error'>已禁用</Tag>
          )}
          {record.fromPlayground && <Tag color='blue'>可测试</Tag>}
        </div>
      ),
    },
  ];

  if (!config || !pricingConfig) {
    return (
      <div className='pricing-table-panel'>
        <div className='pricing-table-loading'>
          <div className='loading-spinner'></div>
          <div>加载中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className='pricing-table-panel'>
      <div className='pricing-table-header'>
        <div className='pricing-table-title-row'>
          {onBack && (
            <button className='pricing-back-button' onClick={onBack}>
              ← 返回
            </button>
          )}
          <h2 className='pricing-table-title'>模型广场</h2>
        </div>
        <div className='pricing-table-filters'>
          <Search
            placeholder='搜索模型名称或厂商'
            allowClear
            style={{ width: 250 }}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Select
            style={{ width: 150 }}
            placeholder='选择用途'
            value={modelCategoryFilter}
            onChange={setModelCategoryFilter}
          >
            <Option value='all'>全部用途</Option>
            {getModelCategories().map((cat) => (
              <Option key={cat} value={cat}>
                {cat}
              </Option>
            ))}
          </Select>
          <Select
            style={{ width: 150 }}
            placeholder='选择厂商'
            value={vendorFilter}
            onChange={setVendorFilter}
          >
            <Option value='all'>全部厂商</Option>
            {getVendors().map((vendor) => (
              <Option key={vendor} value={vendor}>
                {vendor}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <div className='pricing-table-content'>
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个模型`,
          }}
          scroll={{ x: 1200 }}
          size='middle'
        />
      </div>
    </div>
  );
};

export default PricingTablePanel;
